import React, { useCallback, useEffect, useState } from "react";

import { KeyRound, LogOut, Menu, MoreHorizontal, User, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ThemeToggle } from "@/components/common/ThemeToggle";
import {
  useVisibleNavigation,
  type ResolvedNavItem,
} from "@/components/layout/useNavigation";
import { ChangePasswordDialog } from "@/features/auth/components/ChangePasswordDialog";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { LicenseRequestsBell } from "@/features/licenses/components/LicenseRequestsBell";
import { usePendingLicenseRequests } from "@/features/licenses/hooks/useLicenses";
import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import { BREAKPOINTS, useMediaQuery } from "@/shared/hooks/useMediaQuery";

import logoLight from "@/assets/images/logos/logoLightTransparent.png";
/**
 * Props for the Layout component
 */
interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const { can } = usePermissions();
  const navItems = useVisibleNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(BREAKPOINTS.mobile);

  // Close "More" menu when navigating
  useEffect(() => {
    setMoreMenuOpen(false);
  }, [location.pathname]);

  const canManageLicenses = can("licenses:approve_request");

  const { count: pendingLicenseCount } =
    usePendingLicenseRequests(canManageLicenses);
  const getActiveItem = useCallback(
    (item: ResolvedNavItem) => {
      if (item.href === location.pathname) return true;
      if (item.href !== "/" && location.pathname.startsWith(`${item.href}/`)) {
        return true;
      }
      // Fichas de centro: resaltar Usuarios
      if (
        item.href === "/users" &&
        location.pathname.startsWith("/servers/") &&
        !location.pathname.startsWith("/servers/report")
      ) {
        return true;
      }
      return false;
    },
    [location.pathname]
  );

  const getChildActive = useCallback(
    (item: ResolvedNavItem) => {
      return item.children.some((child) => getActiveItem(child));
    },
    [getActiveItem]
  );

  /**
   * Renderiza las entradas del menú lateral, ya filtradas por permisos.
   *
   * El filtrado vive por completo en `useVisibleNavigation`, así que la barra
   * lateral de escritorio, el panel de tableta y la barra inferior de móvil
   * muestran exactamente lo mismo.
   */
  const renderNavigationItems = (
    items: readonly ResolvedNavItem[],
    isMobileSidebar: boolean = false
  ): React.ReactNode => {
    return items.map((item) => {
      const isActive = getActiveItem(item);
      const isChildActive = getChildActive(item);

      return (
        <div key={item.routeId}>
          <Link
            to={item.href}
            className={`flex items-center px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 rounded-lg ${
              isActive
                ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-r-2 border-gray-200 dark:border-gray-700"
                : ""
            }`}
            onClick={() => isMobileSidebar && setSidebarOpen(false)}
          >
            <item.icon className="mr-3 h-5 w-5" />
            <span className="flex-1">{item.name}</span>
            {item.href === "/licenses" && pendingLicenseCount > 0 && (
              <span className="ml-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-semibold text-white">
                {pendingLicenseCount > 99 ? "99+" : pendingLicenseCount}
              </span>
            )}
          </Link>

          {item.children.length > 0 && (
            <div className="ml-6 mt-1 space-y-1">
              {item.children.map((child) => (
                <Link
                  key={child.routeId}
                  to={child.href}
                  className={`flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 rounded-lg ${
                    isChildActive
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-r-2 border-gray-200 dark:border-gray-700"
                      : ""
                  }`}
                  onClick={() => isMobileSidebar && setSidebarOpen(false)}
                >
                  <child.icon className="mr-3 h-4 w-4" />
                  {child.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    });
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      void navigate("/login");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  // Split nav items for bottom bar: primary (max 4) + overflow
  const primaryNavItems = navItems.filter((item) => !item.mobileOverflow);
  const overflowNavItems = navItems.filter((item) => item.mobileOverflow);
  const hasOverflow = overflowNavItems.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* =============================================
          DESKTOP: Sidebar (lg+) — unchanged from original
          ============================================= */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col grow bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
          <div className="flex h-16 items-center px-4 border-b border-gray-200 dark:border-gray-700">
            <img src={logoLight} alt="ViewMed Admin" className="h-7 w-auto" />
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {renderNavigationItems(navItems)}
          </nav>
        </div>
      </div>

      {/* =============================================
          CONTENT AREA
          ============================================= */}
      <div className="lg:pl-64">
        {/* Header */}
        <div className="sticky top-0 z-40 flex h-14 lg:h-16 shrink-0 items-center gap-x-3 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8 dark:border-gray-700 dark:bg-gray-800 safe-top">
          {/* Mobile: show page title or app name */}
          {isMobile ? (
            <div className="flex items-center gap-2">
              <img src={logoLight} alt="ViewMed" className="h-7 w-auto" />
              <span className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                Admin
              </span>
            </div>
          ) : (
            <>
              {/* Tablet: hamburger for sidebar */}
              <button
                type="button"
                className="-m-2.5 p-2.5 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 lg:hidden tap-target"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
              <div className="flex flex-1" />
            </>
          )}

          <div className="flex items-center gap-x-3 lg:gap-x-6">
            <ThemeToggle size="sm" />

            <LicenseRequestsBell enabled={canManageLicenses} />

            {/* Separator - desktop only */}
            <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200 dark:bg-gray-700" />

            {/* User profile */}
            <div className="flex items-center gap-x-3">
              <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <User className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="hidden lg:block">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {currentUser?.email}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setChangePasswordOpen(true)}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-100 tap-target"
                title="Cambiar mi contraseña"
              >
                <KeyRound className="h-5 w-5" />
              </button>
              <button
                onClick={() => void handleLogout()}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-100 tap-target"
                title="Cerrar sesión"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <ChangePasswordDialog
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
        />

        {/* Page content - add bottom padding on mobile for bottom nav */}
        <main className="py-4 lg:py-6">
          <div className="mx-auto max-w-[1876px] px-4 sm:px-6 lg:px-8 pb-20 lg:pb-0">
            {children}
          </div>
        </main>
      </div>

      {/* =============================================
          TABLET: Sidebar overlay (md-lg) — same as original mobile sidebar
          ============================================= */}
      <div
        className={`fixed inset-0 z-50 hidden md:block lg:hidden ${sidebarOpen ? "block!" : "hidden!"}`}
      >
        <div
          className="fixed inset-0 bg-gray-600/75 dark:bg-gray-900/75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white dark:bg-gray-800">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Admin Panel
            </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100 tap-target"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {renderNavigationItems(navItems, true)}
          </nav>
        </div>
      </div>

      {/* =============================================
          MOBILE: Bottom Navigation Bar (<768px)
          ============================================= */}
      {isMobile && (
        <>
          {/* "More" overflow menu backdrop */}
          {moreMenuOpen && (
            <div
              className="fixed inset-0 z-40 bg-black/30"
              onClick={() => setMoreMenuOpen(false)}
            />
          )}

          {/* "More" overflow panel */}
          {moreMenuOpen && (
            <div className="fixed bottom-16 left-0 right-0 z-50 animate-slide-up">
              <div className="bg-white dark:bg-gray-800 rounded-t-3xl shadow-[0_-8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_-8px_30px_rgb(0,0,0,0.3)] border-t border-gray-200 dark:border-gray-700 overflow-hidden pb-4">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                    Más opciones
                  </p>
                  <button
                    onClick={() => setMoreMenuOpen(false)}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-300 transition-colors bg-gray-50 dark:bg-gray-900 p-1 rounded-full"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <nav className="py-2 px-2">
                  {overflowNavItems.map((item) => {
                    const isActive = getActiveItem(item);
                    return (
                      <Link
                        key={item.routeId}
                        to={item.href}
                        className={`flex items-center gap-3 px-4 py-3.5 mx-2 my-1 rounded-xl text-sm transition-colors ${
                          isActive
                            ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 font-semibold"
                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                        }`}
                        onClick={() => setMoreMenuOpen(false)}
                      >
                        <item.icon className="h-5 w-5" />
                        <span className="font-medium">{item.name}</span>
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </div>
          )}

          {/* Bottom Navigation Bar */}
          <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 safe-bottom">
            <div className="flex items-center justify-around h-16 px-2">
              {primaryNavItems.slice(0, hasOverflow ? 3 : 4).map((item) => {
                const isActive = getActiveItem(item) || getChildActive(item);
                return (
                  <Link
                    key={item.routeId}
                    to={item.href}
                    className={`flex flex-col items-center justify-center flex-1 h-full py-1 transition-colors tap-target ${
                      isActive
                        ? "text-primary-600 dark:text-primary-400"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    <div className="shrink-0">
                      <item.icon className="h-5 w-5" />
                      {isActive && (
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-600 dark:bg-primary-400 nav-indicator" />
                      )}
                    </div>
                    <span className="text-[10px] mt-1 font-medium leading-none">
                      {item.name}
                    </span>
                  </Link>
                );
              })}

              {/* "More" button */}
              {hasOverflow && (
                <button
                  onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                  className={`flex flex-col items-center justify-center flex-1 h-full py-1 transition-colors tap-target ${
                    moreMenuOpen || overflowNavItems.some((item) => getActiveItem(item))
                      ? "text-primary-600 dark:text-primary-400"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  <MoreHorizontal className="h-5 w-5" />
                  <span className="text-[10px] mt-1 font-medium leading-none">Más</span>
                </button>
              )}
            </div>
          </nav>
        </>
      )}
    </div>
  );
}
